import { c as _c } from "react/compiler-runtime";
import { SphereTooltipIcon } from "@components/common/sphere-tooltip-icon";
import { InfoIcon, Text } from "@flat-ui";
import { useAreSceneLicensesAvailable } from "@hooks/subscription/use-are-scene-licenses-available";
import { ViewSeatHoldersButton } from "@pages/settings/subscription-settings/seats/view-seat-holders-button";
import { SceneSeatUsage } from "@pages/settings/subscription-settings/software-licenses/scene-seat-usage";
import { limitTooltips } from "@pages/settings/subscription-settings/usage-metrics/limit-tooltips";
import { activeSeatUsageSelector, sceneSeatUsageSelector, selectQuotasSelector } from "@store/sdb-company/sdb-company-selector";
import { Stack } from "@mui-wrapper";
import { useAppSelector } from "@store/store-helper";
import { MEMBER_SEAT_DESCRIPTIONS, MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
/**
 * Custom hook to return the seat sections of the subscription page, each only when the plan
 * includes those seats: the Sphere XG seats assigned to members (the `ACTIVE_SEAT` constraint), the
 * Sphere XG seats of the active users (the active members limit) and the SCENE seats (the
 * `SCENE_SEAT` constraint).
 */
export function useSeatSettings() {
  const $ = _c(4);
  const activeSeatUsage = useAppSelector(activeSeatUsageSelector);
  const activeMembersLimit = useAppSelector(selectQuotasSelector).find(_temp);
  const areSceneLicensesAvailable = useAreSceneLicensesAvailable();
  const sceneSeatUsage = useAppSelector(sceneSeatUsageSelector);
  const seatsSectionProps = [];
  if (activeSeatUsage) {
    let t0;
    if ($[0] !== activeSeatUsage) {
      t0 = createSeatSection({
        usage: activeSeatUsage,
        seat: "xgSeat"
      });
      $[0] = activeSeatUsage;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    seatsSectionProps.push(t0);
  }
  if (activeMembersLimit) {
    seatsSectionProps.push(createSeatSection({
      usage: {
        used: activeMembersLimit.current ?? 0,
        max: activeMembersLimit.limit
      },
      seat: "xgSeat",
      info: limitTooltips.user_role
    }));
  }
  if (areSceneLicensesAvailable && sceneSeatUsage) {
    let t0;
    if ($[2] !== sceneSeatUsage) {
      t0 = createSeatSection({
        usage: sceneSeatUsage,
        seat: "sceneSeat"
      });
      $[2] = sceneSeatUsage;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    seatsSectionProps.push(t0);
  }
  return {
    seatsSectionProps
  };
}
function _temp(t0) {
  const {
    identifier
  } = t0;
  return identifier === "user_role";
}
/** Returns a section showing how many of the seats are taken, with a link to their holders */
function createSeatSection({
  usage,
  seat,
  info
}) {
  return {
    title: `${MEMBER_SEAT_LABELS[seat]} seats`,
    subtitle: MEMBER_SEAT_DESCRIPTIONS[seat],
    items: [{
      label: <Stack direction="row" sx={{
        gap: 1,
        alignItems: "center"
      }}>
            <Text variant="bodyM">In use</Text>
            {info && <SphereTooltipIcon icon={InfoIcon} text={info} dataTestId={`${seat}-usage-info`} />}
          </Stack>,
      content: <SceneSeatUsage usage={usage} actionComponent={<ViewSeatHoldersButton seat={seat} />} />,
      shouldHideTopBorder: true
    }]
  };
}